<template>
  <div>
    <b-card
      v-if="car"
      no-body
      aria-label="Fiche détaillée du véhicule"
      role="main"
    >
      <b-card-header aria-label="En-tête fiche véhicule">
        <div
          class="d-flex justify-content-between align-items-center"
          aria-label="Actions principales"
        >
          <div>
            <h3
              class="card-title"
              aria-label="Identifiant véhicule"
            >
              {{ car.serial_number }}
              <small class="text-muted ml-2">{{ car.brand }} {{ car.model }}</small>
            </h3>
          </div>
          <div class="ml-5">
            <b-button
              v-if="isAdmin"
              :variant="car.is_active ? 'outline-danger' : 'outline-success'"
              :aria-label="car.is_active ? 'Désactiver le véhicule' : 'Activer le véhicule'"
              @click="toggleStatus"
            >
              {{ car.is_active ? 'Désactiver' : 'Activer' }}
            </b-button>
            <b-badge
              :variant="car.is_active ? 'success' : 'danger'"
              class="ml-2"
              aria-label="Statut du véhicule"
            >
              {{ car.is_active ? 'Actif' : 'Inactif' }}
            </b-badge>
          </div>
        </div>
      </b-card-header>

      <b-card-body>
        <b-tabs
          content-class="mt-3"
          aria-label="Sections de la fiche véhicule"
        >
          <!-- Basic Information Tab -->
          <b-tab
            title="Informations de base"
            active
            aria-label="Informations de base du véhicule"
          >
            <b-row>
              <b-col md="6">
                <b-card aria-label="Informations générales du véhicule">
                  <h6 class="mb-2">
                    Informations Générales
                  </h6>
                  <b-list-group flush>
                    <b-list-group-item>
                      <strong>Matricule:</strong> {{ car.serial_number }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Marque:</strong> {{ car.brand }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Modèle:</strong> {{ car.model || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Année:</strong> {{ car.year || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Couleur:</strong> {{ car.color }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Plaque d'immatriculation:</strong> {{ car.plate_number || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>VIN:</strong> {{ car.vin || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Propriétaire:</strong> {{ car.owner || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Statut:</strong>
                      <b-badge :variant="getStatusBadgeVariant(car.status)">
                        {{ getStatusText(car.status) }}
                      </b-badge>
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>

              <b-col md="6">
                <b-card
                  title="Conducteur"
                  aria-label="Informations conducteur affecté"
                >
                  <div
                    v-if="car.default_driver"
                    aria-label="Conducteur assigné"
                  >
                    <div class="d-flex align-items-center mb-2">
                      <b-avatar
                        :text="getDriverInitials(car.default_driver)"
                        variant="primary"
                        size="4rem"
                      />
                      <div class="ml-2">
                        <h4 class="mb-0">
                          {{ car.default_driver.first_name }} {{ car.default_driver.last_name }}
                        </h4>
                        <div class="text-muted">
                          {{ car.default_driver.email }}
                        </div>
                      </div>
                    </div>
                    <b-list-group flush>
                      <b-list-group-item>
                        <strong>Téléphone:</strong> {{ car.default_driver.phone || 'Non défini' }}
                      </b-list-group-item>
                      <b-list-group-item>
                        <strong>Rôle:</strong> {{ car.default_driver.role || 'Conducteur' }}
                      </b-list-group-item>
                    </b-list-group>
                    <div class="mt-3">
                      <b-button
                        v-if="user.role === 'admin'"
                        variant="outline-danger"
                        size="sm"
                        @click="removeDriver"
                      >
                        <feather-icon
                          icon="UserXIcon"
                          class="mr-1"
                          size="16"
                        />
                        Retirer le conducteur
                      </b-button>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-center py-4"
                    aria-label="Aucun conducteur assigné"
                  >
                    <feather-icon
                      icon="UserIcon"
                      size="36"
                      class="text-muted mb-2"
                    />
                    <h5>Aucun conducteur assigné</h5>
                    <p class="text-muted">
                      Cette voiture n'a pas de conducteur assigné.
                    </p>
                    <b-button
                      v-if="isAdmin"
                      variant="primary"
                      size="sm"
                      @click="openDriverModal"
                    >
                      <feather-icon
                        icon="UserPlusIcon"
                        class="mr-1"
                        size="16"
                      />
                      Assigner un conducteur
                    </b-button>
                  </div>
                </b-card>
              </b-col>
            </b-row>

            <!-- Rental Information Section -->
            <b-row class="mt-3">
              <b-col md="6">
                <b-card aria-label="Informations de location">
                  <div class="d-flex align-items-center mb-3">
                    <feather-icon
                      icon="KeyIcon"
                      size="20"
                      class="text-primary mr-2"
                    />
                    <h6 class="mb-0">
                      Informations de location
                    </h6>
                  </div>
                  <b-list-group flush>
                    <b-list-group-item>
                      <strong>Statut:</strong>
                      <b-badge
                        :variant="car.is_rented ? 'success' : 'secondary'"
                        class="ml-2"
                      >
                        {{ car.is_rented ? 'En location' : 'Non louée' }}
                      </b-badge>
                    </b-list-group-item>
                    <b-list-group-item v-if="car.is_rented">
                      <strong>Montant mensuel:</strong>
                      <span class="text-success font-weight-bold ml-2">
                        {{ car.monthly_rental_amount ? `${parseFloat(car.monthly_rental_amount).toFixed(2)} TND` : 'Non défini' }}
                      </span>
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Technical Specifications Tab -->
          <b-tab
            title="Spécifications techniques"
            aria-label="Spécifications techniques du véhicule"
          >
            <b-row>
              <b-col md="6">
                <b-card aria-label="Caractéristiques techniques du véhicule">
                  <h6 class="mb-2">
                    Caractéristiques Techniques
                  </h6>
                  <b-list-group flush>
                    <b-list-group-item>
                      <strong>Cylindrée moteur:</strong> {{ car.engine_size ? `${car.engine_size} L` : 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Transmission:</strong> {{ car.transmission || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Type de carburant:</strong> {{ car.fuel_type || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Nombre de portes:</strong> {{ car.doors || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Nombre de sièges:</strong> {{ car.seats || 'Non défini' }}
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>

              <b-col md="6">
                <b-card aria-label="Capacité et poids du véhicule">
                  <h6 class="mb-2">
                    Capacité et Poids
                  </h6>
                  <b-list-group flush>
                    <b-list-group-item>
                      <strong>Capacité:</strong> {{ car.capacity ? `${car.capacity} m³` : 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Poids maximal:</strong> {{ car.max_weight ? `${car.max_weight} kg` : 'Non défini' }}
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Maintenance Tab -->
          <b-tab
            title="Entretien et Conformité"
            aria-label="Entretien et conformité du véhicule"
          >
            <b-row>
              <b-col md="6">
                <b-card aria-label="Informations maintenance du véhicule">
                  <h6 class="mb-2">
                    Maintenance
                  </h6>
                  <b-list-group flush>
                    <b-list-group-item>
                      <strong>Kilométrage:</strong> {{ car.mileage ? `${car.mileage} km` : 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Date d'immatriculation:</strong> {{ formatDate(car.registration_date) }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Dernier entretien:</strong> {{ formatDate(car.last_maintenance_date) }}
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Prochain entretien:</strong> {{ formatDate(car.next_maintenance_date) }}
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>

              <b-col md="6">
                <b-card aria-label="Documents légaux du véhicule">
                  <h6 class="mb-2">
                    Documents Légaux
                  </h6>
                  <b-list-group flush>
                    <b-list-group-item>
                      <strong>Expiration d'assurance:</strong>
                      <div class="d-flex justify-content-between align-items-center">
                        <span>{{ formatDate(car.insurance_expiry) }}</span>
                        <b-badge :variant="getExpiryStatus(car.insurance_expiry)">
                          {{ getExpiryText(car.insurance_expiry) }}
                        </b-badge>
                      </div>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Expiration contrôle technique:</strong>
                      <div class="d-flex justify-content-between align-items-center">
                        <span>{{ formatDate(car.technical_inspection_expiry) }}</span>
                        <b-badge :variant="getExpiryStatus(car.technical_inspection_expiry)">
                          {{ getExpiryText(car.technical_inspection_expiry) }}
                        </b-badge>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Description Tab -->
          <b-tab
            title="Description"
            aria-label="Description du véhicule"
          >
            <b-card aria-label="Zone de description du véhicule">
              <p v-if="car.description">
                {{ car.description }}
              </p>
              <p
                v-else
                class="text-muted"
              >
                Aucune description disponible
              </p>
            </b-card>
          </b-tab>

          <!-- Roadmaps Tab -->
          <b-tab
            title="Feuilles de Route"
            aria-label="Feuilles de route du véhicule"
          >
            <b-card aria-label="Section feuilles de route du véhicule">
              <h5 class="mb-3">
                Feuilles de route d'aujourd'hui
              </h5>
              <b-table
                v-if="todayRoadmaps.length > 0"
                :items="todayRoadmaps"
                :fields="roadmapFields"
                striped
                responsive
                hover
                aria-label="Tableau des feuilles de route du jour pour ce véhicule"
              >
                <template #cell(actions)="data">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :to="{ name: 'roadmap-detail', params: { id: data.item.id } }"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="16"
                    />
                  </b-button>
                </template>

                <template #cell(status)="data">
                  <b-badge :variant="data.item.is_payed ? 'success' : 'warning'">
                    {{ data.item.is_payed ? 'Payé' : 'Non payé' }}
                  </b-badge>
                </template>
              </b-table>
              <p
                v-else
                class="text-muted text-center my-4"
              >
                Aucune feuille de route créée aujourd'hui pour ce véhicule
              </p>

              <hr class="my-4">

              <h5 class="mb-3">
                Dernières feuilles de route
              </h5>
              <b-table
                v-if="latestRoadmaps.length > 0"
                :items="latestRoadmaps"
                :fields="roadmapFields"
                striped
                responsive
                hover
                aria-label="Tableau des dernières feuilles de route pour ce véhicule"
              >
                <template #cell(actions)="data">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :to="{ name: 'roadmap-detail', params: { id: data.item.id } }"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="16"
                    />
                  </b-button>
                </template>

                <template #cell(status)="data">
                  <b-badge :variant="data.item.is_payed ? 'success' : 'warning'">
                    {{ data.item.is_payed ? 'Payé' : 'Non payé' }}
                  </b-badge>
                </template>
              </b-table>
              <p
                v-else
                class="text-muted text-center my-4"
              >
                Aucune feuille de route trouvée pour ce véhicule
              </p>
            </b-card>
          </b-tab>
        </b-tabs>
      </b-card-body>
    </b-card>
    <div
      v-else
      class="text-center p-5"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner variant="primary" />
      <p class="mt-2">
        Chargement des détails du véhicule...
      </p>
    </div>

    <!-- Modal for assigning driver -->
    <b-modal
      id="assign-driver-modal"
      ref="assignDriverModal"
      title="Assigner un conducteur"
      ok-title="Assigner"
      cancel-variant="outline-secondary"
      aria-label="Modal d’assignation conducteur"
      aria-describedby="assign-driver-help"
      @ok="handleDriverAssigned"
    >
      <b-form-group
        label="Sélectionner un conducteur"
        label-for="driver-select"
      >
        <v-select
          v-model="selectedDriver"
          :options="availableDrivers"
          label="name"
          :reduce="driver => driver.id"
          placeholder="Sélectionner un conducteur"
          input-id="driver-select"
          aria-label="Sélectionner un conducteur à affecter au véhicule"
        />
      </b-form-group>
      <div
        id="assign-driver-help"
        class="sr-only"
      >
        Choisissez un conducteur actif à affecter à ce véhicule. Cette action mettra à jour l’affectation dans le système.
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BListGroup,
  BListGroupItem,
  BBadge,
  BButton,
  BSpinner,
  BModal,
  BFormGroup,
  BAvatar,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BListGroup,
    BListGroupItem,
    BBadge,
    BButton,
    BSpinner,
    BModal,
    BFormGroup,
    BAvatar,
    vSelect,
  },
  data() {
    return {
      car: null,
      isLoading: true,
      user: {},
      selectedDriver: null,
      availableDrivers: [],
      todayRoadmaps: [],
      latestRoadmaps: [],
      roadmapFields: [
        { key: 'id', label: 'ID' },
        { key: 'created_at', label: 'Date de création' },
        { key: 'delivery_man.username', label: 'Livreur' },
        { key: 'orders_count', label: "Nombre d'ordres" },
        { key: 'status', label: 'Statut' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  computed: {
    isAdmin() {
      return this.user.role === 'admin'
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.fetchCarDetails()
  },
  methods: {
    async fetchCarDetails() {
      try {
        const { data } = await axios.get(`/api/cars/detail/${this.$route.params.id}/`)
        this.car = data

        // After fetching car details, load roadmap information
        await this.fetchRoadmaps()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching car details:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des détails de la voiture')
      } finally {
        this.isLoading = false
      }
    },

    async fetchRoadmaps() {
      try {
        // Get current date in YYYY-MM-DD format for filtering
        const today = new Date()
        const todayFormatted = today.toISOString().split('T')[0]

        // Fetch roadmaps for this car
        const { data } = await axios.get('/api/roadmaps/', {
          params: {
            car: this.car.serial_number,
          },
        })

        // Filter today's roadmaps
        this.todayRoadmaps = data.filter(roadmap => {
          const roadmapDate = new Date(roadmap.created_at).toISOString().split('T')[0]
          return roadmapDate === todayFormatted
        }).map(this.formatRoadmapData)

        // Get latest 5 roadmaps
        this.latestRoadmaps = data
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5)
          .map(this.formatRoadmapData)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching roadmaps:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des feuilles de route')
      }
    },

    formatRoadmapData(roadmap) {
      return {
        ...roadmap,
        created_at: new Date(roadmap.created_at).toLocaleString('fr-FR'),
        orders_count: roadmap.orders ? roadmap.orders.length : 0,
      }
    },

    async toggleStatus() {
      try {
        // Show confirmation dialog before proceeding
        const result = await this.$swal({
          title: this.car.is_active ? 'Désactiver la voiture?' : 'Activer la voiture?',
          text: this.car.is_active
            ? 'Êtes-vous sûr de vouloir désactiver cette voiture?'
            : 'Êtes-vous sûr de vouloir activer cette voiture?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, confirmer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        // If user confirmed the action
        if (result.value) {
          const { data } = await axios.post(`/api/cars/toggle-status/${this.$route.params.id}/`, {
            is_active: !this.car.is_active,
          })

          this.car.is_active = data.is_active
          this.showToast(
            'success',
            'top-center',
            `La voiture a été ${data.is_active ? 'activée' : 'désactivée'} avec succès`,
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error toggling car status:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      }
    },

    async openDriverModal() {
      // Load available drivers
      try {
        // Change API endpoint to the correct one
        const { data } = await axios.get('/api/auth/drivers/', {
          params: { is_active: true },
        })

        // Map the data using the username field from the new format
        this.availableDrivers = data.map(driver => ({
          id: driver.id,
          name: driver.username, // Use username as the display name
          car: driver.car, // Store car info for reference if needed
        }))

        this.$refs.assignDriverModal.show()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading drivers:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des conducteurs')
      }
    },

    getDriverInitials(driver) {
      if (!driver || !driver.username) return 'NA'

      // Split the username by spaces and get initials
      const parts = driver.username.split(' ')
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`
      } if (parts.length === 1) {
        return parts[0].charAt(0)
      }
      return 'NA'
    },

    async handleDriverAssigned() {
      if (!this.selectedDriver) return

      try {
        await axios.put(`/api/cars/update/${this.$route.params.id}/`, {
          ...this.car,
          default_driver: this.selectedDriver, // Changed from driver to default_driver
        })

        this.fetchCarDetails() // Reload to get updated driver details
        this.showToast('success', 'top-center', 'Conducteur assigné avec succès')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error assigning driver:', error)
        this.showToast('danger', 'top-center', 'Erreur lors de l\'assignation du conducteur')
      }
    },

    async removeDriver() {
      try {
        // Show confirmation dialog before proceeding
        const result = await this.$swal({
          title: 'Retirer le conducteur?',
          text: 'Êtes-vous sûr de vouloir retirer ce conducteur de la voiture?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, retirer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })

        // If user confirmed the action
        if (result.value) {
          await axios.put(`/api/cars/update/${this.$route.params.id}/`, {
            ...this.car,
            default_driver: null, // Changed from driver to default_driver
          })

          this.fetchCarDetails() // Reload to get updated data
          this.showToast('success', 'top-center', 'Conducteur retiré avec succès')
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error removing driver:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du retrait du conducteur')
      }
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },

    // Helper functions for displaying new fields
    formatDate(date) {
      if (!date) return 'Non défini'
      return new Date(date).toLocaleDateString('fr-FR')
    },

    getExpiryStatus(date) {
      if (!date) return 'light'

      const expiryDate = new Date(date)
      const today = new Date()
      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(today.getDate() + 30)

      if (expiryDate < today) {
        return 'danger'
      } if (expiryDate < thirtyDaysFromNow) {
        return 'warning'
      }
      return 'success'
    },

    getExpiryText(date) {
      if (!date) return ''

      const expiryDate = new Date(date)
      const today = new Date()
      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(today.getDate() + 30)

      if (expiryDate < today) {
        return 'Expiré'
      } if (expiryDate < thirtyDaysFromNow) {
        return 'Expire bientôt'
      }
      return 'Valide'
    },

    getStatusText(status) {
      const statusMap = {
        available: 'Disponible',
        in_use: 'En utilisation',
        maintenance: 'En maintenance',
        out_of_service: 'Hors service',
      }
      return statusMap[status] || status || 'Disponible'
    },

    getStatusBadgeVariant(status) {
      const variantMap = {
        available: 'success',
        in_use: 'primary',
        maintenance: 'warning',
        out_of_service: 'danger',
      }
      return variantMap[status] || 'secondary'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

// Card title styling
.card-title {
  small {
    font-size: 0.65em;
  }
}

// Consistent form styling for modal
.form-control, .vs__dropdown-toggle {
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #114092;
    box-shadow: none;
  }
}

// Add some styling for the tabs and detail sections
.nav-tabs {
  border-bottom: 1px solid #ddd;

  .nav-link {
    &.active {
      color: #114092;
      border-bottom: 2px solid #114092;
    }
  }
}

.list-group-item {
  display: flex;
  justify-content: space-between;

  strong {
    margin-right: 1rem;
  }
}
</style>
